import React, { useState, MouseEvent, JSX } from 'react';
import { observer } from 'mobx-react';
import { Badge } from 'reactstrap';
import dayjs from 'dayjs';

import {
  ApiPackageModel,
  ApiTravelDateCandidate,
  ApiPackageDetails,
  ApiDurationType,
  ApiBestPricePackageModel
} from '@ibe/api';

import { useTheme } from '@emotion/react';
import { LoggerFactory } from '@ibe/services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import PackageDurations from './PackageDurations/PackageDurations';
import { Select } from '../../../Components/Form/Select/Select';
import ListItemLayout from '../../../Components/Layouts/ListItemLayout/ListItemLayout';
import { ItemComponentProps } from '../../../../Models/ItemRegistry';
import useTranslation from '../../../Hooks/useTranslation';
import fallback from '../../../../_Translations/generated/PackageListItem.en.t.json';
import Keys from '../../../../_Translations/generated/PackageListItem.en.t.json.keys';
import ThreeColumnListItemLayout from '../../../Components/Layouts/ListItemLayout/ThreeColumnListItemLayout/ThreeColumnListItemLayout';
import { PriceListItemLayout } from '../../../Components/Layouts/ListItemLayout/ThreeColumnListItemLayout/PriceListItemLayout';
import PackageParams from '../../../SubModules/PackageDetails/PackageParams';

export type PackageItemSelection = {
  item: ApiPackageModel | ApiBestPricePackageModel;
  travelDate: ApiTravelDateCandidate;
  detail: ApiPackageDetails;
};

type Props = ItemComponentProps & {
  onSelect?: (selection: Partial<PackageItemSelection>) => void;
  showMultipleDurations?: boolean;
  eachItemSearchParams?: PackageParams;
};

const logger = LoggerFactory.get('PackageListItem');

const PackageListItem = observer((props: Props) => {
  const { t } = useTranslation('PackageListItem', fallback, Keys);
  const theme = useTheme();

  const showMultipleDurations = !!props.showMultipleDurations;
  const onSelect = props.onSelect || logger.warn;

  const item = props.item as ApiPackageModel;
  const category = item.category?.description || '';
  const description = item.shortDescription || '';
  const imageUrl = item.media?.mainImage?.small.url || theme.placeholderImages.package;
  const image = item.media?.mainImage?.small || undefined;
  const { name } = item;
  const location =
    item.geoAssignment && item.geoAssignment.location ? item.geoAssignment.location : null;

  const singleDuration = item.packageDetails && item.packageDetails[0].packageDurationInfo;

  const travelDates = item.travelDates || []; // TODO fix api - this should not be allow to be undefined

  const [travelDate, setTravelDate] = useState<ApiTravelDateCandidate>(travelDates[0]);

  const getTravelDateLabel = (date: ApiTravelDateCandidate): string =>
    showMultipleDurations
      ? dayjs(date.startDate).format('MMM D, YYYY')
      : [dayjs(date.startDate).format('MMM D'), dayjs(date.endDate).format('MMM D, YYYY')].join(
          ' - '
        );

  const onDetailSelect = (detail: ApiPackageDetails): void => {
    const selection: PackageItemSelection = { item, detail, travelDate };
    onSelect(selection);
  };

  const handleClick = (e: MouseEvent<HTMLSpanElement>): void => {
    e.stopPropagation();
    const selectedDetail = item.packageDetails?.find(d => d.travelDateId === travelDate.id);
    if (selectedDetail) {
      onDetailSelect(selectedDetail);
    }
  };

  const filteredDetails = item.packageDetails?.filter(
    detail => detail.travelDateId === travelDate.id
  );

  const Center = (): JSX.Element => (
    <div className="d-flex flex-column h-100">
      <div className="flex-grow-1">
        <PackageLocation />
        {description && <div>{description}</div>}
      </div>
      <div className="mt-3 mb-2">
        <div className="text-primary">{t(Keys.travelDates)}</div>
        <div onClick={(e: MouseEvent<HTMLDivElement>): void => e.stopPropagation()}>
          <Select
            options={travelDates}
            selectedOption={travelDate}
            onChange={(date): void => setTravelDate(date)}
            getLabel={(date): string => getTravelDateLabel(date)}
            getValue={(date): string => date.id}
          />
        </div>
      </div>
    </div>
  );

  const PackageBtn = (): JSX.Element => (
    <button
      type="button"
      className="iso__package-item__button btn btn-primary"
      onClick={handleClick}
    >
      {t(Keys.button)}
    </button>
  );

  const PackagePrice = (): JSX.Element => (
    <>
      <PriceListItemLayout
        totalPrice={travelDate.price}
        showTaxText
        isTotalFromPrice
        customTaxText={t(Keys.packagePriceInfo)}
        customPrefix={t(Keys.pricePerAdultFrom)}
      />
    </>
  );

  const PackageLocation = (): JSX.Element | null =>
    location ? (
      <div className="mb-3">
        <FontAwesomeIcon icon={faMapMarkerAlt} /> {location}
      </div>
    ) : null;

  const DurationAndType = (): JSX.Element => (
    <div className="text-left text-md-right">
      {singleDuration && (
        <div>
          {singleDuration.type === ApiDurationType.DAY
            ? t(Keys.days, { duration: singleDuration.duration })
            : t(Keys.nights, { duration: singleDuration.duration })}
        </div>
      )}
      <div>{category}</div>
    </div>
  );

  return (
    <>
      {showMultipleDurations && (
        <ListItemLayout
          headline={name}
          center={<Center />}
          footer={
            filteredDetails && (
              <PackageDurations
                durations={filteredDetails}
                onSelect={(d): void => onDetailSelect(d)}
              />
            )
          }
          image={imageUrl}
          topRight={<Badge color="primary">{category}</Badge>}
          placeholderImage={theme.placeholderImages.package}
          onClick={handleClick}
          fullAreaClick
        />
      )}
      {!showMultipleDurations && (
        <ThreeColumnListItemLayout
          onClick={handleClick}
          image={image}
          center={<Center />}
          headline={name}
          topRight={<DurationAndType />}
          midRight={<PackagePrice />}
          button={<PackageBtn />}
          placeholderImage={theme.placeholderImages.package}
          fullAreaClick
        />
      )}
    </>
  );
});

export default PackageListItem;
